import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import Countdown from './partials/CountDown';
import { CopyBigSkin } from '../../atoms/Copytext/CopytextSkins';
import CTA from '../../atoms/CTA';
import { CTAIconPosition } from '../../atoms/CTA/CTA.constants';
import Icon from '../../atoms/Icon';
import Container from '~/grid/Container';

const StyledCounter = styled(Container)`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 30px;
  background: ${(props) => props.theme.color.gradientprimary500};
  align-items: center;
  justify-content: space-between;

  span + span {
    margin: 0;
  }

  @media (${(props) => props.theme.eventCounter.mobileSwitch}) {
    flex-direction: column;
    align-items: flex-start;
    padding-top: 40px;
    padding-bottom: 40px;
  }
`;

const StyledIcon = styled(Icon)`
  color: ${(props) => props.theme.color.white500};
`;

const CounterDescription = styled.span`
  ${CopyBigSkin};
  color: ${(props) => props.theme.color.white500};
`;

const TextContainer = styled.div`
  display: flex;
  width: ${(props) => (props.counterDone ? '100%' : '30%')};
  align-items: flex-start;
  justify-content: ${(props) =>
    props.counterDone ? 'center' : 'space-between'};
  flex-wrap: nowrap;
  flex-direction: ${(props) => (props.counterDone ? 'row' : 'row-reverse')};

  ${(props) =>
    props.counterDone &&
    css`
      margin-left: 100px;
    `};

  svg {
    margin: ${(props) => (props.counterDone ? '-4px 25px 0 0' : '-4px 25px')};
    @media (${(props) => props.theme.eventCounter.mobileSwitch}) {
      margin: 0px 15px 0 0;
    }
  }
  span {
    align-self: center;
  }

  span + span {
    margin: 0;
  }
  @media (${(props) => props.theme.eventCounter.mobileSwitch}) {
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;

    ${(props) =>
      props.counterDone &&
      css`
        margin-left: 0;
      `};
  }
`;

const StyledCTA = styled(CTA)`
  flex-shrink: 0;
  &&& > a:hover,
  &&& > a:hover span {
    color: ${(props) => props.theme.color.white500};
  }

  &&& > a:active:hover,
  &&& > a:active:hover span {
    color: ${(props) => props.theme.color.white500};
  }
  &&& > a span {
    color: ${(props) => props.theme.color.cta};
  }
  @media (${(props) => props.theme.eventCounter.mobileSwitch}) {
    margin-top: 25px;
    width: 100%;
    > a {
      width: 100%;
      justify-content: space-between;
    }
  }
`;

// ToDo: Add tests
/**
 * @param {string} date
 * @returns {boolean}
 */
const isInFuture = (date) => moment(date).isAfter(moment());

/**
 * @param {string} date
 * @returns {DurationType}
 */
const getTimeDifference = (date) => {
  const now = moment();
  const eventDate = moment(date);
  const diff = moment.duration(eventDate.diff(now));
  return {
    days: Math.floor(diff.asDays()),
    hours: diff.hours(),
    minutes: diff.minutes(),
    seconds: diff.seconds(),
  };
};

/**
 * @param {EventCounterProps} props
 * @returns {React.ReactElement | null} The rendered component
 */
const EventCounter = ({
  startTimeStamp,
  endTimeStamp,
  textBeforeEvent,
  textDuringEvent,
  linkBeforeEvent = undefined,
  linkDuringEvent,
  icon = 'CLOCK',
}) => {
  const [counterExpired, setCounterExpired] = useState(
    !isInFuture(endTimeStamp),
  );
  const [counterActive, setCounterActive] = useState(
    isInFuture(startTimeStamp),
  );
  const [timeDifference, setTimeDifference] = useState(
    getTimeDifference(startTimeStamp),
  );

  const eventLink = counterActive ? linkBeforeEvent : linkDuringEvent;

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCounterExpired(!isInFuture(endTimeStamp));
    if (!counterExpired && counterActive) {
      setTimeout(() => {
        setCounterActive(isInFuture(startTimeStamp));
        setTimeDifference(getTimeDifference(startTimeStamp));
      }, 1000);
    }
  });
  /* eslint-enable react-hooks/exhaustive-deps */

  if (counterExpired) {
    return null;
  }

  return (
    <StyledCounter>
      {counterActive && (
        <>
          <TextContainer counterDone={false}>
            <StyledIcon icon={icon} />
            <CounterDescription>{textBeforeEvent}</CounterDescription>
          </TextContainer>
          <Countdown {...timeDifference} />
        </>
      )}
      {!counterActive && (
        <TextContainer counterDone={true}>
          <StyledIcon icon={icon} />
          <CounterDescription>{textDuringEvent}</CounterDescription>
        </TextContainer>
      )}
      {eventLink && (
        <StyledCTA
          skin="SECONDARY"
          href={eventLink.href}
          title={eventLink.title}
          text={eventLink.text}
          target={eventLink.target || '_self'}
          icon="ARROW_ANIMATION"
          iconPos={CTAIconPosition.After}
        />
      )}
    </StyledCounter>
  );
};

export default EventCounter;
